import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import {
  ActionDialog,
  ReasonModal,
  ShortfallModal,
} from '../../components/dialogs';
import { HistorySection, opnameHistory } from '../../components/history';
import {
  DetailHeader,
  DetailSkeleton,
  KV,
  linkCls,
  LoadError,
  Section,
  StatusBadge,
} from '../../components/ui';
import { fmtDateTime } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import { WAREHOUSE_PATHS } from '../../paths';
import { OPNAME_STATUS, OPNAME_TYPE_META } from '../../status';
import type { ShortfallRow, StockOpnameDetail } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { DiscrepancySummary } from './DiscrepancySummary';
import { fmtGap } from './opnameDraft';
import { OpnameLinesTable } from './OpnameLinesTable';

type BusyKind = 'approve' | 'reject' | 'fatApprove' | 'fatReject' | 'cancel';
/** How an action ended; `handled` = the caller showed the failure itself. */
type Outcome = 'done' | 'failed' | 'handled' | 'busy';

/** Who did it and when: "ops@dash.co · 12 Oct, 09:00". */
const byAt = (by: string | null, at: string | null): string | null =>
  by ? `${by}${at ? ` · ${fmtDateTime(at)}` : ''}` : null;

const StockOpnameDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getStockOpname(id, { signal }),
    [id, refreshToken]
  );
  const [busy, setBusy] = useState<BusyKind | null>(null);
  const busyRef = useRef(false);
  const [rejecting, setRejecting] = useState<'manager' | 'fat' | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [fatApproving, setFatApproving] = useState(false);
  const [shortfall, setShortfall] = useState<{
    rows: ShortfallRow[];
    message: string;
  } | null>(null);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.stockOpname}
          backLabel="Stock opname"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Stock opname tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }
  const o = detail.data;

  /**
   * One action at a time. The answer is the document's new state; a failure
   * is a toast (and a reload when the status moved under us).
   */
  const run = async (
    kind: BusyKind,
    call: () => Promise<StockOpnameDetail>,
    success: string,
    onFail?: (err: unknown) => boolean
  ): Promise<Outcome> => {
    if (busyRef.current) return 'busy';
    busyRef.current = true;
    setBusy(kind);
    try {
      const next = await call();
      detail.setData(next);
      toast.success(success, { description: o.code });
      return 'done';
    } catch (err) {
      if (onFail?.(err)) return 'handled';
      const e = parseWarehouseError(err);
      toast.error(e.message);
      // Someone else acted first: show the document as it is now.
      if (e.status === 409) detail.reload();
      return 'failed';
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const approve = () =>
    run(
      'approve',
      () => warehouseApi.approveStockOpname(o.id),
      'Disetujui manager, menunggu FAT',
      (err) => {
        const e = parseWarehouseError(err);
        if (e.code !== 'INSUFFICIENT_STOCK') return false;
        setShortfall({ rows: e.shortfall, message: e.message });
        return true;
      }
    );
  const fatApprove = async () => {
    const outcome = await run(
      'fatApprove',
      () => warehouseApi.fatApproveStockOpname(o.id),
      'Disetujui FAT, stok disesuaikan'
    );
    // Leave the dialog whatever the outcome: a failure is a toast on the page.
    if (outcome !== 'busy') setFatApproving(false);
  };
  const reject = async (reason: string) => {
    const by = rejecting;
    if (!by) return;
    setRejectError(null);
    const outcome = await run(
      by === 'fat' ? 'fatReject' : 'reject',
      () =>
        by === 'fat'
          ? warehouseApi.fatRejectStockOpname(o.id, reason)
          : warehouseApi.rejectStockOpname(o.id, reason),
      by === 'fat' ? 'Ditolak FAT' : 'Ditolak manager',
      (err) => {
        const e = parseWarehouseError(err);
        if (e.status !== 422) return false;
        setRejectError(e.fieldErrors[0]?.message ?? e.message);
        return true;
      }
    );
    if (outcome === 'done' || outcome === 'failed') setRejecting(null);
  };

  /** The requester withdraws it while it waits, at either approval stage. */
  const cancel = async (reason: string) => {
    setCancelError(null);
    const outcome = await run(
      'cancel',
      () => warehouseApi.cancelStockOpname(o.id, reason),
      'Pengajuan dibatalkan',
      (err) => {
        const e = parseWarehouseError(err);
        if (e.status !== 422) return false;
        setCancelError(e.fieldErrors[0]?.message ?? e.message);
        return true;
      }
    );
    if (outcome === 'done' || outcome === 'failed') setCancelling(false);
  };

  const idle = busy === null;
  const rejected =
    o.status === 'REJECTED_BY_MANAGER' || o.status === 'REJECTED_BY_FAT';
  const waiting =
    o.status === 'WAITING_MANAGER_APPROVAL' ||
    o.status === 'WAITING_FAT_APPROVAL';
  /** The reason is on the "Pengajuan dibatalkan" timeline event (newest first). */
  const cancelEvent =
    o.status === 'CANCELLED'
      ? o.events.find((e) => e.status === 'CANCELLED')
      : undefined;

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.stockOpname}
        backLabel="Stock opname"
        meta={
          <>
            Diajukan {fmtDateTime(o.createdAt)} · oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {o.createdBy}
            </span>
          </>
        }
        title={o.code}
        badges={
          <>
            <StatusBadge meta={OPNAME_STATUS[o.status]} />
            <StatusBadge meta={OPNAME_TYPE_META[o.type]} />
          </>
        }
        actions={
          <>
            {waiting && (
              <Button
                tone="destructive"
                style="ghost"
                data-testid="opname-cancel"
                disabled={!idle}
                onClick={() => {
                  setCancelError(null);
                  setCancelling(true);
                }}
                leftIcon={<i className="ri-forbid-line text-[14px]" />}
              >
                Batalkan pengajuan
              </Button>
            )}
            {o.status === 'WAITING_MANAGER_APPROVAL' && (
              <>
                <Button
                  tone="destructive"
                  style="ghost"
                  data-testid="opname-reject"
                  disabled={!idle}
                  onClick={() => {
                    setRejectError(null);
                    setRejecting('manager');
                  }}
                  leftIcon={<i className="ri-close-circle-line text-[14px]" />}
                >
                  Tolak
                </Button>
                <Button
                  data-testid="opname-approve"
                  loading={busy === 'approve'}
                  disabled={!idle}
                  onClick={() => void approve()}
                  leftIcon={<i className="ri-check-line text-[14px]" />}
                >
                  Approve manager
                </Button>
              </>
            )}
            {o.status === 'WAITING_FAT_APPROVAL' && (
              <>
                <Button
                  tone="destructive"
                  style="ghost"
                  data-testid="opname-fat-reject"
                  disabled={!idle}
                  onClick={() => {
                    setRejectError(null);
                    setRejecting('fat');
                  }}
                  leftIcon={<i className="ri-close-circle-line text-[14px]" />}
                >
                  Tolak (FAT)
                </Button>
                <Button
                  data-testid="opname-fat-approve"
                  disabled={!idle}
                  onClick={() => setFatApproving(true)}
                  leftIcon={<i className="ri-check-double-line text-[14px]" />}
                >
                  Approve FAT
                </Button>
              </>
            )}
          </>
        }
      />

      {o.status === 'CANCELLED' && (
        <Alert
          status="information"
          appearance="stroke"
          size="lg"
          title="Pengajuan dibatalkan"
          data-testid="opname-cancelled"
        >
          {cancelEvent?.note || '—'}
          {cancelEvent &&
            ` · ${fmtDateTime(cancelEvent.occurredAt)} · ${cancelEvent.actor}`}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section
            title="Ringkasan selisih"
            description="Fisik dikurangi sistem, dijumlahkan dari semua baris"
          >
            <DiscrepancySummary totals={o} />
          </Section>
          <Section
            title="Daftar produk"
            description={`${o.lineCount} baris · ${o.gapLineCount} berselisih`}
          >
            <OpnameLinesTable lines={o.lines} testId="opname-lines" />
          </Section>
          <HistorySection entries={opnameHistory(o.events)} />
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Informasi">
            <KV label="Client" value={o.client?.name ?? `Client #${o.clientID}`} />
            <KV label="Hub" value={o.hub?.name ?? `Hub #${o.hubID}`} />
            <KV label="Pemohon" value={o.createdBy} />
            <KV label="Catatan" value={o.notes} />
            <KV
              label="Berita acara"
              value={
                o.reportUrl ? (
                  <a
                    href={o.reportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={linkCls}
                    data-testid="opname-report-link"
                  >
                    Buka PDF
                  </a>
                ) : null
              }
            />
            {byAt(o.approvedBy, o.approvedAt) && (
              <KV
                label="Disetujui manager"
                value={byAt(o.approvedBy, o.approvedAt)}
              />
            )}
            {byAt(o.fatApprovedBy, o.fatApprovedAt) && (
              <KV
                label="Disetujui FAT"
                value={byAt(o.fatApprovedBy, o.fatApprovedAt)}
              />
            )}
            {rejected && (
              <>
                <KV
                  label="Ditolak oleh"
                  value={byAt(o.rejectedBy, o.rejectedAt)}
                />
                <KV label="Alasan penolakan" value={o.rejectReason} />
              </>
            )}
          </Section>
        </div>
      </div>

      <ReasonModal
        open={rejecting !== null}
        title={rejecting === 'fat' ? 'Tolak opname (FAT)' : 'Tolak opname'}
        subtitle={o.code}
        notice={
          rejecting === 'fat'
            ? 'Stok yang dicadangkan untuk baris kurang dilepas kembali. Stok tidak disesuaikan.'
            : 'Opname ditutup tanpa penyesuaian stok.'
        }
        confirmLabel={rejecting === 'fat' ? 'Tolak (FAT)' : 'Tolak opname'}
        placeholder="Mis. hitungan perlu diulang karena selisih terlalu besar"
        busy={busy === 'reject' || busy === 'fatReject'}
        error={rejectError}
        testId={
          rejecting === 'fat'
            ? 'opname-fat-reject-dialog'
            : 'opname-reject-dialog'
        }
        onConfirm={(reason) => void reject(reason)}
        onClose={() => setRejecting(null)}
      />
      <ReasonModal
        open={cancelling}
        title="Batalkan pengajuan"
        subtitle={o.code}
        notice={
          o.status === 'WAITING_FAT_APPROVAL'
            ? 'Stok yang dicadangkan untuk baris kurang dilepas kembali. Opname ditutup tanpa penyesuaian stok.'
            : 'Opname ditutup tanpa penyesuaian stok.'
        }
        confirmLabel="Batalkan pengajuan"
        placeholder="Mis. salah memilih client atau hitungan belum selesai"
        busy={busy === 'cancel'}
        error={cancelError}
        testId="opname-cancel-dialog"
        onConfirm={(reason) => void cancel(reason)}
        onClose={() => setCancelling(false)}
      />
      <ActionDialog
        open={fatApproving}
        title="Approve FAT"
        subtitle={o.code}
        description={`Stok disesuaikan sekarang mengikuti hitungan fisik: selisih ${fmtGap(o.totalGap)} pada ${o.gapLineCount} baris dicatat sebagai penyesuaian di ledger stok. Tindakan ini tidak dapat dibatalkan.`}
        confirmLabel="Approve & sesuaikan stok"
        busy={busy === 'fatApprove'}
        testId="opname-fat-approve-dialog"
        onConfirm={() => void fatApprove()}
        onClose={() => setFatApproving(false)}
      />
      <ShortfallModal
        open={!!shortfall}
        orderCode={o.code}
        subtitle="Stok belum cukup untuk dicadangkan, opname tetap menunggu manager"
        rows={shortfall?.rows ?? []}
        message={shortfall?.message}
        onClose={() => setShortfall(null)}
      />
    </div>
  );
};

export default StockOpnameDetailPage;
